import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';

// OTPInput component (inlined to avoid circular dependency)
const OTPInput = ({ length = 6, value, onChange }) => {
  const digits = value.split('');
  while (digits.length < length) digits.push('');

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 1);
    const newDigits = [...digits];
    newDigits[idx] = val;
    onChange(newDigits.join(''));
    if (val && idx < length - 1) {
      document.getElementById(`otp-${idx + 1}`)?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      document.getElementById(`otp-${idx - 1}`)?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted);
    e.preventDefault();
  };

  return (
    <div className="otp-input-container">
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          id={`otp-${idx}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[idx] || ''}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          onPaste={handlePaste}
          className="otp-box"
        />
      ))}
    </div>
  );
};

const COUNTRY_CODES = [
  { code: '+1',    flag: '🇺🇸', label: 'United States',  short: 'US' },
  { code: '+91',   flag: '🇮🇳', label: 'India',          short: 'IN' },
  { code: '+44',   flag: '🇬🇧', label: 'United Kingdom', short: 'UK' },
  { code: '+61',   flag: '🇦🇺', label: 'Australia',      short: 'AU' },
  { code: '+971',  flag: '🇦🇪', label: 'UAE',            short: 'AE' },
  { code: '+65',   flag: '🇸🇬', label: 'Singapore',      short: 'SG' },
  { code: '+60',   flag: '🇲🇾', label: 'Malaysia',       short: 'MY' },
];

const LoginModal = ({ isOpen, onClose }) => {
  const { login } = useAuth();

  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[1]); // default India
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const confirmationResultRef = useRef(null);
  const verifierRef = useRef(null);
  const [registerData, setRegisterData] = useState({ first_name: '', last_name: '', email: '' });
  const firebaseIdTokenRef = useRef('');

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('phone');
      setPhoneNumber('');
      setOtp('');
      setError('');
      setRegisterData({ first_name: '', last_name: '', email: '' });
      setCountdown(0);
    }
  }, [isOpen]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  // Cleanup verifier on unmount
  useEffect(() => {
    return () => {
      if (verifierRef.current) {
        try { verifierRef.current.clear(); } catch (_) {}
        verifierRef.current = null;
      }
    };
  }, []);

  const formatPhoneUS = (phone) => {
    const c = phone.replace(/\D/g, '');
    if (c.length <= 3) return c;
    if (c.length <= 6) return `(${c.slice(0,3)}) ${c.slice(3)}`;
    return `(${c.slice(0,3)}) ${c.slice(3,6)}-${c.slice(6,10)}`;
  };

  const handlePhoneChange = (text) => {
    const cleaned = text.replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(selectedCountry.code === '+1' ? formatPhoneUS(cleaned) : cleaned);
  };

  const getCleanPhone = () => phoneNumber.replace(/\D/g, '');
  const getE164Phone = () => `${selectedCountry.code}${getCleanPhone()}`;
  const isPhoneValid = () => getCleanPhone().length >= 7;

  const filteredCountries = COUNTRY_CODES.filter(c =>
    c.label.toLowerCase().includes(countrySearch.toLowerCase()) || c.code.includes(countrySearch)
  );

  const getVerifier = () => {
    if (verifierRef.current) {
      try { verifierRef.current.clear(); } catch (_) {}
      verifierRef.current = null;
    }
    verifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container-modal', {
      size: 'invisible',
    });
    return verifierRef.current;
  };

  const handleSendOTP = async () => {
    if (!isPhoneValid()) { setError('Please enter a valid phone number'); return; }
    setError('');
    setIsLoading(true);
    try {
      const verifier = getVerifier();
      const confirmationResult = await signInWithPhoneNumber(auth, getE164Phone(), verifier);
      confirmationResultRef.current = confirmationResult;
      setStep('otp');
      setCountdown(60);
    } catch (e) {
      console.error('[OTP] sendOTP error:', e.code, e.message);
      if (verifierRef.current) {
        try { verifierRef.current.clear(); } catch (_) {}
        verifierRef.current = null;
      }
      if (e.code === 'auth/invalid-phone-number') {
        setError('Invalid phone number. Please check the number and country code.');
      } else if (e.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setError(e.message || 'Failed to send OTP. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length < 6) { setError('Please enter the 6-digit code'); return; }
    if (!confirmationResultRef.current) { setError('Session expired. Please go back and resend OTP.'); return; }
    setError('');
    setIsLoading(true);
    try {
      const userCredential = await confirmationResultRef.current.confirm(otp);
      const firebaseIdToken = await userCredential.user.getIdToken();

      const res = await apiService.firebasePhoneAuth(firebaseIdToken);
      if (!res.success) { setError(res.error || 'Authentication failed. Please try again.'); return; }

      const data = res.data || {};
      if (data.is_new_user) {
        firebaseIdTokenRef.current = firebaseIdToken;
        setStep('register');
      } else {
        login(data.access_token, data.customer || {});
        onClose();
      }
    } catch (e) {
      console.error('Firebase verify error:', e.code, e.message);
      if (e.code === 'auth/invalid-verification-code') {
        setError('Incorrect OTP. Please check and try again.');
      } else if (e.code === 'auth/code-expired') {
        setError('OTP has expired. Please go back and request a new code.');
      } else {
        setError(e.message || 'Verification failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteRegistration = async () => {
    if (!registerData.first_name.trim()) { setError('First name is required'); return; }
    if (!registerData.last_name.trim()) { setError('Last name is required'); return; }
    setError('');
    setIsLoading(true);
    try {
      const res = await apiService.completeFirebaseRegistration({
        firebase_token: firebaseIdTokenRef.current,
        first_name: registerData.first_name.trim(),
        last_name: registerData.last_name.trim(),
        country_code: selectedCountry.code,
      });
      if (res.success) {
        login(res.data?.access_token, res.data?.customer || {});
        onClose();
      } else {
        setError(res.error || 'Registration failed. Please try again.');
      }
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay login-modal-overlay" onClick={onClose}>
      {/* Invisible reCAPTCHA anchor */}
      <div id="recaptcha-container-modal" />

      <div className="login-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="modal-close-btn" onClick={onClose}>✕</button>

        {/* Logo */}
        <div className="auth-logo">
          <span className="auth-logo-icon">🍛</span>
          <h1 className="auth-brand">Biryani</h1>
          <p className="auth-tagline">Authentic Home Kitchens</p>
        </div>

        {/* Step: phone */}
        {step === 'phone' && !showCountryPicker && (
          <>
            <h2 className="auth-title">Welcome Back</h2>
            <p className="auth-subtitle">Enter your phone number to continue</p>

            <div className="phone-input-wrapper">
              <button className="country-code-btn" onClick={() => setShowCountryPicker(true)}>
                {selectedCountry.flag} {selectedCountry.code}
              </button>
              <input
                type="tel"
                className="phone-input"
                placeholder="Phone number"
                value={phoneNumber}
                onChange={(e) => handlePhoneChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendOTP()}
              />
            </div>

            {error && <p className="auth-error">{error}</p>}

            <button
              className="btn-primary btn-full"
              onClick={handleSendOTP}
              disabled={isLoading || !isPhoneValid()}
            >
              {isLoading ? 'Sending…' : 'Send OTP'}
            </button>

            <p className="auth-footer-text">
              By continuing, you agree to our Terms and Privacy Policy
            </p>
          </>
        )}

        {/* Country picker */}
        {showCountryPicker && (
          <>
            <button className="back-btn" onClick={() => setShowCountryPicker(false)}>← Back</button>
            <h2 className="auth-title">Select Country</h2>
            <input
              type="text"
              className="form-input"
              placeholder="Search country..."
              value={countrySearch}
              onChange={(e) => setCountrySearch(e.target.value)}
              style={{ marginBottom: '16px' }}
            />
            <div className="country-list">
              {filteredCountries.map(country => (
                <button
                  key={country.code}
                  className={`country-item ${selectedCountry.code === country.code ? 'selected' : ''}`}
                  onClick={() => { setSelectedCountry(country); setShowCountryPicker(false); }}
                >
                  <span>{country.flag}</span>
                  <span>{country.label}</span>
                  <span className="country-code">{country.code}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Step: otp */}
        {step === 'otp' && (
          <>
            <button className="back-btn" onClick={() => { setStep('phone'); setOtp(''); setError(''); }}>← Back</button>
            <h2 className="auth-title">Verify OTP</h2>
            <p className="auth-subtitle">
              We sent a 6-digit code to {selectedCountry.code} {phoneNumber}
            </p>

            <OTPInput length={6} value={otp} onChange={setOtp} />

            {error && <p className="auth-error">{error}</p>}

            <button
              className="btn-primary btn-full"
              onClick={handleVerifyOTP}
              disabled={isLoading || otp.length < 6}
            >
              {isLoading ? 'Verifying…' : 'Verify & Continue'}
            </button>

            <div className="resend-row">
              {countdown > 0 ? (
                <p className="resend-countdown">Resend OTP in {countdown}s</p>
              ) : (
                <button className="resend-btn" onClick={handleSendOTP} disabled={isLoading}>
                  Resend OTP
                </button>
              )}
            </div>
          </>
        )}

        {/* Step: register */}
        {step === 'register' && (
          <>
            <button className="back-btn" onClick={() => { setStep('otp'); setError(''); }}>← Back</button>
            <h2 className="auth-title">Complete Your Profile</h2>
            <p className="auth-subtitle">Tell us a bit about yourself</p>

            <div className="form-group">
              <label className="form-label">First Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter your first name"
                value={registerData.first_name}
                onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter your last name"
                value={registerData.last_name}
                onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email (Optional)</label>
              <input
                type="email"
                className="form-input"
                placeholder="Enter your email"
                value={registerData.email}
                onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
              />
            </div>

            {error && <p className="auth-error">{error}</p>}

            <button
              className="btn-primary btn-full"
              onClick={handleCompleteRegistration}
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account…' : 'Complete Registration'}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default LoginModal;
